import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

// eslint-disable-next-line no-shadow
export enum UserLevels {
    /* eslint-disable @typescript-eslint/naming-convention */
    None,
    SuperAdministrator,
    Administrator,
    User,
    Observer,
    /* eslint-enable */
}

/*@Injectable()*/
export class Globals {
    static messages = [
        { id: '000', description: 'Connection error. Please try again later' },
        { id: '001', description: 'Error connecting to the database' },
        { id: '002', description: 'Incorrect username or password' },
        { id: '003', description: 'Specified e-mail address is already registered' },
        { id: '004', description: 'Your account is not confirmed.' },
        { id: '005', description: 'Your account is already confirmed.' },
        { id: '006', description: 'You are not authorized to perform the selected operation.' },
        { id: '007', description: 'The are still some references.' },
        { id: '008', description: 'This user already has access to the tree.' },
        { id: '009', description: 'The user has been granted access to the tree.' },
        { id: '010', description: 'The invitation has been sent.' },
        { id: '011', description: 'The selected profile does not belong to this tree.' },
        { id: '012', description: 'You cannot change your own access to the tree.' },
        { id: '014', description: 'Enter a valid e-mail address to invite a new user.' },
        { id: '015', description: 'The invitation is invalid, expired or already used.' },
        { id: '016', description: 'The invitation has been accepted.' },
        { id: '017', description: 'This username is already taken.' },
        { id: '018', description: 'Enter a valid e-mail address and a password of at least 6 characters.' },
        { id: '019', description: 'Account created. Check your e-mail to confirm it.' },
        { id: '020', description: 'The typed user name does not match your account.' },
        { id: '021', description: 'You are the only super administrator of a shared tree. Grant those rights to somebody else first.' },
        { id: '022', description: 'Your account has been deleted.' },
        { id: '023', description: 'Invalid date format.' },
        { id: '024', description: 'The event was not saved - there is no person to link it to.' },
        { id: '025', description: 'You must accept the terms of service and the privacy policy.' },
        { id: '026', description: 'This tree address is already taken.' },
        { id: '027', description: 'The tree address may contain lowercase letters, digits, hyphen and underscore only (3-50 characters).' },
        { id: '028', description: 'The tree name is required.' },
        // 029-031 maja tlumaczenia w slownikach (status.029...) - interceptor
        // najpierw szuka klucza i18n, ponizsze opisy to tylko awaryjny fallback
        { id: '029', description: 'The tree has reached the person limit of its plan. Upgrade the plan to add more people.' },
        { id: '030', description: 'The tree has reached the storage limit of its plan. Upgrade the plan to add more files.' },
        { id: '031', description: 'This GEDCOM file would exceed the person limit of the tree plan.' },
        { id: '032', description: 'The tree exceeds the person limit of its plan, so editing is locked. Upgrade the plan or remove some persons.' },
        { id: '033', description: 'Payments are temporarily unavailable. Please try again later.' },
        { id: '034', description: 'Relationship search is part of the paid plans. Upgrade the tree plan to use it.' },
        { id: '035', description: 'Relationship search is available to tree members only. Sign in with an account that belongs to this tree.' },
        { id: '036', description: 'Inviting collaborators is part of the paid plans. Upgrade the tree plan to work on it together.' },
        { id: '037', description: 'The message could not be sent. Both of you have to be members of this tree.' },
        { id: '038', description: 'The question could not be sent. Check the e-mail address, or try again later.' },
        { id: '039', description: 'This confirmation link is invalid or has expired. Please write the message again.' },
        { id: '040', description: 'This file is not a supported image. Upload a JPG, PNG, GIF, BMP or WEBP file.' },
        { id: '041', description: 'This account cannot be deleted from the admin panel.' },
        { id: '042', description: 'A new password has been sent to the user\'s e-mail address.' },
        { id: '043', description: 'The user account has been deleted.' },
        { id: '044', description: 'The account data has been saved.' },
        { id: '045', description: 'The confirmation link has been sent again.' },
        { id: '046', description: 'The e-mail could not be sent. Check the account\'s address and try again.' }
    ];

    /* Nazwa w tym języku, którego dotyczy - tak jak w przełączniku języka
     * interfejsu (patrz i18n/language.ts). Dopasowanie idzie zawsze po id,
     * description służy wyłącznie do wyświetlania. */
    static languages = [
        { id: 'en', description: 'English' },
        { id: 'pl', description: 'Polski' },
        { id: 'de', description: 'Deutsch' },
        { id: 'fr', description: 'Français' },
        { id: 'ru', description: 'Русский' }
    ];

    /* Klucze tłumaczeń - poziom uprawnień pokazuje się w wybranym języku. */
    static adminLevels = [
        'level.superAdmin',
        'level.admin',
        'level.user',
        'level.observer'
    ];

    static getLanguageObject(obj: { language: string }[]) {
        let valObj = obj.find((val) => val.language === environment.language);
        if (!valObj) {
            valObj = obj.find((val) => val.language === 'en');
        }
        if (!valObj) {
            valObj = obj.length > 0 ? obj[0] : undefined;
        }
        return valObj;
    }

    static getMessageDescription(findMessage: string) {
        return this.messages.find(message => message.id === findMessage)?.description ?? '';
    }

    static getLanguageDescription(findLanguage: string) {
        return this.languages.find(language => language.id === findLanguage)?.description ?? '';
    }

    static getMembershipLevel(level: number) {
        return this.adminLevels[level - 1];
    }

    /** Poziomy uprawnień w drzewie do wyboru na listach rozwijanych */
    static getMembershipLevels() {
        return this.adminLevels.map((description, index) => ({ level: index + 1, description }));
    }

    /* --- Dziennik zmian (audit_log) ------------------------------------------
     * Wpisy przychodzą z bazy jako nazwy tabel i kolumn - poniższe słowniki
     * zamieniają je na tekst zrozumiały dla użytkownika. */

    static auditOperations: Record<string, string> = {
        I: 'audit.op.I',
        U: 'audit.op.U',
        D: 'audit.op.D',
        N: 'audit.op.N'
    };

    static auditTables: Record<string, string> = {
        person: 'audit.table.person',
        person_names: 'audit.table.person_names',
        event: 'audit.table.event',
        event_persons: 'audit.table.event_persons',
        event_places: 'audit.table.event_places',
        family: 'audit.table.family',
        family_children: 'audit.table.family_children',
        source: 'audit.table.source',
        citation: 'audit.table.citation',
        event_sources: 'audit.table.event_sources',
        event_citations: 'audit.table.event_citations',
        person_sources: 'audit.table.person_sources',
        person_citations: 'audit.table.person_citations',
        tree: 'audit.table.tree'
    };

    static auditFields: Record<string, string> = {
        sex: 'audit.field.sex',
        alive: 'audit.field.alive',
        language: 'audit.field.language',
        languages: 'audit.field.languages',
        textid: 'audit.field.textid',
        pos: 'audit.field.pos',
        nametype: 'audit.field.nametype',
        prefix: 'audit.field.prefix',
        name: 'audit.field.name',
        name_prefix: 'audit.field.name_prefix',
        surname: 'audit.field.surname',
        surname_type: 'audit.field.surname_type',
        surname_prefix: 'audit.field.surname_prefix',
        surname_suffix: 'audit.field.surname_suffix',
        isdefault: 'audit.field.isdefault',
        event_type: 'audit.field.event_type',
        date: 'audit.field.date',
        date_type: 'audit.field.date_type',
        time: 'audit.field.time',
        time_type: 'audit.field.time_type',
        description: 'audit.field.description',
        family: 'audit.field.family',
        parent_id: 'audit.field.parent_id',
        event_id: 'audit.field.event_id',
        person_id: 'audit.field.person_id',
        person_type: 'audit.field.person_type',
        place_id: 'audit.field.place_id',
        husband_id: 'audit.field.husband_id',
        wife_id: 'audit.field.wife_id',
        husband_order: 'audit.field.husband_order',
        wife_order: 'audit.field.wife_order',
        family_id: 'audit.field.family_id',
        child_id: 'audit.field.child_id',
        sort: 'audit.field.sort',
        child_type: 'audit.field.child_type',
        source_id: 'audit.field.source_id',
        citation_id: 'audit.field.citation_id',
        title: 'audit.field.title',
        text: 'audit.field.text',
        number: 'audit.field.number',
        year: 'audit.field.year',
        pages: 'audit.field.pages',
        years: 'audit.field.years',
        author: 'audit.field.author',
        publisher: 'audit.field.publisher',
        link: 'audit.field.link',
        type: 'audit.field.type'
    };

    /** Etykiety pól zależne od tabeli - mają pierwszeństwo przed ogólnymi. */
    static auditFieldsByTable: Record<string, Record<string, string>> = {
        source: { name: 'audit.source.name', type: 'audit.source.type' },
        citation: { type: 'audit.citation.type', text: 'audit.citation.text' }
    };

    /* Wartości to KLUCZE tłumaczeń - tekst dobiera I18nService przy wyświetlaniu.
       Inaczej dziennik zmian zostałby po polsku niezależnie od wybranego języka. */
    static auditEventTypes: Record<string, string> = {
        birth: 'event.birth',
        death: 'event.death',
        burial: 'event.burial',
        marriage: 'event.marriage',
        divorce: 'event.divorce',
        occupation: 'event.occupation',
        title: 'event.title',
        dynasty: 'event.dynasty',
        coatofarms: 'event.coatofarms'
    };

    /** Wartości słownikowe kolumn - inaczej w dzienniku widać surowe 'married'. */
    static auditValues: Record<string, Record<string, string>> = {
        event_type: Globals.auditEventTypes,
        surname_type: {
            birth: 'audit.value.surname_type.birth',
            married: 'audit.value.surname_type.married',
            knownas: 'audit.value.surname_type.knownas'
        },
        child_type: {
            birth: 'audit.value.child_type.birth',
            adopted: 'audit.value.child_type.adopted',
            foster: 'audit.value.child_type.foster'
        },
        person_type: {
            main: 'audit.value.person_type.main'
        },
        sex: {
            M: 'audit.value.sex.M',
            W: 'audit.value.sex.W'
        }
    };

    static getAuditOperation(operation: string) {
        return this.auditOperations[operation] ?? operation;
    }

    static getAuditTable(tableName: string) {
        return this.auditTables[tableName] ?? tableName;
    }

    static getAuditField(field: string, tableName?: string) {
        // ta sama nazwa kolumny znaczy co innego w roznych tabelach
        // (person_names.name to imie, source.name to nazwa zrodla)
        const perTable = tableName ? this.auditFieldsByTable[tableName]?.[field] : undefined;
        return perTable ?? this.auditFields[field] ?? field;
    }

    static getAuditEventType(eventType: string) {
        return this.auditEventTypes[eventType] ?? eventType;
    }

    static getAuditValue(field: string, value: string) {
        return this.auditValues[field]?.[value] ?? value;
    }

}
